import { Button, ResponsiveTable, useTranslation } from '@hbs-us/ui';
import {
  type ChangeEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { openCredentialed } from '../../../lib/download.js';
import { ConfirmDialog } from '../../_money/ConfirmDialog.js';
import { useMoneyFormatters } from '../../_money/useMoneyFormatters.js';
import type { JobDto, JobExpenseRow, JobIncomeRow } from '../contractsMirror.js';
import { expenseReceiptUrl } from '../fileLinks.js';
import {
  type ExpenseInput,
  pollReceiptOcr,
  type ReceiptSuggestions,
  useAddExpenseMutation,
  useAddIncomeMutation,
  useArchiveExpenseMutation,
  useArchiveIncomeMutation,
  useJobExpensesQuery,
  useJobIncomeQuery,
  useJobOpenInvoicesQuery,
  useRestoreExpenseMutation,
  useRestoreIncomeMutation,
  useUpdateExpenseMutation,
  useUploadReceiptMutation,
} from '../useJobQuery.js';

/**
 * The Expenses / Income tab body — add expense + add income forms and the running totals, plus
 * archive (delete) confirmation through the focus-trapped `_money/ConfirmDialog`.
 *
 * Every mutation rides `apiFetch` (double-submit CSRF on the POST) and carries NO tenant id — the
 * server resolves the tenant from the principal and re-checks the Admin/Manager gate (SEC-11 /
 * T-22-privesc). On success the job detail is invalidated so the Profitability tab + the rollups
 * re-sync to the authoritative server numbers. Every color is a locked token (no hex).
 */

export function ExpenseIncomeDialogs({ job }: { job: JobDto }) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();
  const jobId = job.id;

  const addExpense = useAddExpenseMutation(jobId);
  const addIncome = useAddIncomeMutation(jobId);
  const archiveExpense = useArchiveExpenseMutation(jobId);
  const archiveIncome = useArchiveIncomeMutation(jobId);
  const restoreExpense = useRestoreExpenseMutation(jobId);
  const restoreIncome = useRestoreIncomeMutation(jobId);
  const updateExpense = useUpdateExpenseMutation(jobId);

  const incomeQuery = useJobIncomeQuery(jobId);
  const expensesQuery = useJobExpensesQuery(jobId);

  const activeIncome = incomeQuery.data?.income ?? [];
  const archivedIncome = incomeQuery.data?.archivedIncome ?? [];
  const activeExpenses = expensesQuery.data?.expenses ?? [];
  const archivedExpenses = expensesQuery.data?.archivedExpenses ?? [];

  const [confirming, setConfirming] = useState<{ kind: 'expense' | 'income'; id: number } | null>(
    null,
  );
  const [editing, setEditing] = useState<JobExpenseRow | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  function confirmArchive() {
    if (!confirming) return;
    const target = confirming;
    setConfirming(null);
    if (target.kind === 'expense') archiveExpense.mutate({ expenseId: target.id });
    else archiveIncome.mutate({ incomeId: target.id });
  }

  const hasArchived = archivedIncome.length > 0 || archivedExpenses.length > 0;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <AddExpenseForm
        pending={addExpense.isPending}
        error={addExpense.isError ? t('jobs:expenseIncome.addError') : null}
        onSubmit={(input) => addExpense.mutate(input)}
      />
      <AddIncomeForm
        jobId={jobId}
        pending={addIncome.isPending}
        error={addIncome.isError ? t('jobs:expenseIncome.addError') : null}
        onSubmit={(input) => addIncome.mutate(input)}
      />

      <div className="rounded-md border border-border bg-card p-6 lg:col-span-2">
        <h3 className="mb-2 font-semibold text-foreground text-lg">
          {t('jobs:expenseIncome.totalsHeading')}
        </h3>
        <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground text-xs uppercase tracking-wide">
              {t('jobs:expenseIncome.totalIncome')}
            </dt>
            <dd className="mt-1 font-mono font-semibold text-foreground">
              {fmt.formatCurrency(Number(job.financials?.totalIncome ?? 0))}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs uppercase tracking-wide">
              {t('jobs:expenseIncome.totalExpenses')}
            </dt>
            <dd className="mt-1 font-mono font-semibold text-foreground">
              {fmt.formatCurrency(Number(job.financials?.totalExpenses ?? 0))}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs uppercase tracking-wide">
              {t('jobs:expenseIncome.totalLabor')}
            </dt>
            <dd className="mt-1 font-mono font-semibold text-foreground">
              {fmt.formatCurrency(Number(job.financials?.totalLabor ?? 0))}
            </dd>
          </div>
        </dl>
      </div>

      {/* Individual expense rows — the previously-dead archive hook is now reachable. */}
      <div className="rounded-md border border-border bg-card p-6 lg:col-span-2">
        <h3 className="mb-3 font-semibold text-foreground text-lg">
          {t('jobs:expenseIncome.expenseRowsHeading')}
        </h3>
        <ExpenseRowsTable
          rows={activeExpenses}
          onEdit={(row) => setEditing(row)}
          onArchive={(id) => setConfirming({ kind: 'expense', id })}
        />
      </div>

      {/* Individual income rows. */}
      <div className="rounded-md border border-border bg-card p-6 lg:col-span-2">
        <h3 className="mb-3 font-semibold text-foreground text-lg">
          {t('jobs:expenseIncome.incomeRowsHeading')}
        </h3>
        <IncomeRowsTable
          rows={activeIncome}
          onArchive={(id) => setConfirming({ kind: 'income', id })}
        />
      </div>

      {/* Collapsed Archived section — restore an income or expense row. */}
      {hasArchived ? (
        <div className="rounded-md border border-border bg-card p-6 lg:col-span-2">
          <button
            type="button"
            onClick={() => setShowArchived((prev) => !prev)}
            aria-expanded={showArchived}
            className="flex w-full items-center justify-between font-semibold text-foreground text-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <span>{t('jobs:expenseIncome.archivedHeading')}</span>
            <span className="text-muted-foreground text-sm">
              {showArchived
                ? t('jobs:expenseIncome.hideArchived')
                : t('jobs:expenseIncome.showArchived')}
            </span>
          </button>
          {showArchived ? (
            <div className="mt-4 space-y-6">
              {archivedExpenses.length > 0 ? (
                <ArchivedExpenseRows
                  rows={archivedExpenses}
                  onRestore={(id) => restoreExpense.mutate({ expenseId: id })}
                />
              ) : null}
              {archivedIncome.length > 0 ? (
                <ArchivedIncomeRows
                  rows={archivedIncome}
                  onRestore={(id) => restoreIncome.mutate({ incomeId: id })}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirming !== null}
        heading={t('jobs:expenseIncome.deleteHeading')}
        body={t('jobs:expenseIncome.deleteBody')}
        confirmLabel={t('jobs:expenseIncome.deleteConfirm')}
        cancelLabel={t('jobs:expenseIncome.deleteCancel')}
        confirmPending={archiveExpense.isPending || archiveIncome.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={confirmArchive}
      />

      {editing ? (
        <EditExpenseDialog
          expense={editing}
          pending={updateExpense.isPending}
          error={updateExpense.isError ? t('jobs:expenseIncome.editError') : null}
          onCancel={() => setEditing(null)}
          onSave={(input) =>
            updateExpense.mutate(
              { expenseId: editing.id, input },
              { onSuccess: () => setEditing(null) },
            )
          }
        />
      ) : null}
    </div>
  );
}

/**
 * The active expense rows table with per-row Edit + Archive actions. `ResponsiveTable`
 * reflows the rows to label:value cards below `md`; `md`+ is unchanged.
 */
function ExpenseRowsTable({
  rows,
  onEdit,
  onArchive,
}: {
  rows: JobExpenseRow[];
  onEdit: (row: JobExpenseRow) => void;
  onArchive: (id: number) => void;
}) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();

  if (rows.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('jobs:expenseIncome.noExpenses')}</p>;
  }

  return (
    <ResponsiveTable>
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">{t('jobs:expenseIncome.expenseRowsCaption')}</caption>
        <thead>
          <tr className="border-border border-b text-muted-foreground text-xs uppercase tracking-wide">
            <th scope="col" className="px-3 py-2 font-medium">
              {t('jobs:expenseIncome.date')}
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              {t('jobs:expenseIncome.category')}
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              {t('jobs:expenseIncome.vendor')}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t('jobs:expenseIncome.amount')}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t('jobs:expenseIncome.colActions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-border/60 border-b last:border-b-0">
              <td className="px-3 py-2 font-mono text-foreground">{row.date}</td>
              <td className="px-3 py-2 text-foreground">
                {row.category || t('jobs:expenseIncome.none')}
              </td>
              <td className="px-3 py-2 text-foreground">
                {row.vendor || t('jobs:expenseIncome.none')}
              </td>
              <td className="px-3 py-2 text-right font-mono text-foreground">
                {fmt.formatCurrency(Number(row.amount))}
              </td>
              <td className="px-3 py-2 text-right">
                <div className="flex flex-wrap items-center justify-end gap-1">
                  {row.hasReceipt ? <ViewReceiptButton expenseId={row.id} /> : null}
                  <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(row)}>
                    {t('jobs:expenseIncome.edit')}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => onArchive(row.id)}>
                    {t('jobs:expenseIncome.archive')}
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}

/**
 * The active income rows table with a per-row Archive action. `ResponsiveTable`
 * reflows the rows to label:value cards below `md`; `md`+ is unchanged.
 */
function IncomeRowsTable({
  rows,
  onArchive,
}: {
  rows: JobIncomeRow[];
  onArchive: (id: number) => void;
}) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();

  if (rows.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('jobs:expenseIncome.noIncome')}</p>;
  }

  return (
    <ResponsiveTable>
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">{t('jobs:expenseIncome.incomeRowsCaption')}</caption>
        <thead>
          <tr className="border-border border-b text-muted-foreground text-xs uppercase tracking-wide">
            <th scope="col" className="px-3 py-2 font-medium">
              {t('jobs:expenseIncome.date')}
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              {t('jobs:expenseIncome.description')}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t('jobs:expenseIncome.amount')}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t('jobs:expenseIncome.colActions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-border/60 border-b last:border-b-0">
              <td className="px-3 py-2 font-mono text-foreground">{row.date}</td>
              <td className="px-3 py-2 text-foreground">
                {row.description || t('jobs:expenseIncome.none')}
              </td>
              <td className="px-3 py-2 text-right font-mono text-foreground">
                {fmt.formatCurrency(Number(row.amount))}
              </td>
              <td className="px-3 py-2 text-right">
                <Button type="button" variant="ghost" size="sm" onClick={() => onArchive(row.id)}>
                  {t('jobs:expenseIncome.archive')}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}

/**
 * Opens the receipt stored against an expense.
 *
 * The image lives on the api host and is only served to a credentialed request, so neither a
 * plain link nor a direct image source works: the SPA holds its access token in memory and a
 * navigation cannot carry it, and the tenant-host CSP allows `'self' data: blob:` only, with no
 * api origin. It has to be fetched through `apiFetch` and handed to the browser as a blob, which
 * is exactly what `openCredentialed` does.
 *
 * The fetch takes a moment on a phone, so the button reports that it is working and says so when
 * it fails. A control that looks dead on click is worse than no control.
 */
function ViewReceiptButton({ expenseId }: { expenseId: number }) {
  const { t } = useTranslation();
  const [state, setState] = useState<'idle' | 'opening' | 'failed'>('idle');

  const open = useCallback(async () => {
    setState('opening');
    try {
      await openCredentialed(expenseReceiptUrl(expenseId));
      setState('idle');
    } catch {
      setState('failed');
    }
  }, [expenseId]);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={state === 'opening'}
        onClick={open}
        aria-describedby={state === 'failed' ? `receipt-error-${expenseId}` : undefined}
      >
        {state === 'opening'
          ? t('jobs:expenseIncome.receiptOpening')
          : t('jobs:expenseIncome.viewReceipt')}
      </Button>
      {state === 'failed' ? (
        <span id={`receipt-error-${expenseId}`} role="alert" className="text-destructive text-xs">
          {t('jobs:expenseIncome.receiptOpenError')}
        </span>
      ) : null}
    </>
  );
}

/** Archived expense rows with a Restore action each. */
function ArchivedExpenseRows({
  rows,
  onRestore,
}: {
  rows: JobExpenseRow[];
  onRestore: (id: number) => void;
}) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();
  return (
    <div>
      <h4 className="mb-2 font-medium text-muted-foreground text-sm">
        {t('jobs:expenseIncome.archivedExpenses')}
      </h4>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm"
          >
            <span className="text-foreground">
              <span className="font-mono">{row.date}</span> ·{' '}
              {row.category || t('jobs:expenseIncome.none')} ·{' '}
              <span className="font-mono">{fmt.formatCurrency(Number(row.amount))}</span>
            </span>
            <span className="flex flex-wrap items-center justify-end gap-1">
              {row.hasReceipt ? <ViewReceiptButton expenseId={row.id} /> : null}
              <Button type="button" variant="secondary" size="sm" onClick={() => onRestore(row.id)}>
                {t('jobs:expenseIncome.restore')}
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Archived income rows with a Restore action each. */
function ArchivedIncomeRows({
  rows,
  onRestore,
}: {
  rows: JobIncomeRow[];
  onRestore: (id: number) => void;
}) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();
  return (
    <div>
      <h4 className="mb-2 font-medium text-muted-foreground text-sm">
        {t('jobs:expenseIncome.archivedIncome')}
      </h4>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm"
          >
            <span className="text-foreground">
              <span className="font-mono">{row.date}</span> ·{' '}
              {row.description || t('jobs:expenseIncome.none')} ·{' '}
              <span className="font-mono">{fmt.formatCurrency(Number(row.amount))}</span>
            </span>
            <Button type="button" variant="secondary" size="sm" onClick={() => onRestore(row.id)}>
              {t('jobs:expenseIncome.restore')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Focus-trapped edit-expense dialog (full field shape → PATCH /api/v1/expenses/:id). */
function EditExpenseDialog({
  expense,
  pending,
  error,
  onCancel,
  onSave,
}: {
  expense: JobExpenseRow;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: {
    category: string;
    vendor: string | null;
    amount: number;
    date: string;
  }) => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const [category, setCategory] = useState(expense.category ?? '');
  const [vendor, setVendor] = useState(expense.vendor ?? '');
  const [amount, setAmount] = useState(String(expense.amount ?? ''));
  const [date, setDate] = useState(expense.date ?? '');

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    },
    [onCancel],
  );

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>('input,select,button')?.focus();
    return () => previouslyFocused.current?.focus();
  }, []);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSave({
      category: category.trim(),
      vendor: vendor.trim() || null,
      amount: Number(amount),
      date,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onKeyDown={handleKeyDown}
        className="w-full max-w-md rounded-md border border-border bg-card p-6 shadow-lg"
      >
        <h2 id={headingId} className="font-display font-semibold text-foreground text-xl">
          {t('jobs:expenseIncome.editHeading')}
        </h2>
        <form onSubmit={submit} className="mt-4 space-y-3">
          {error ? (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field id="edit-expense-amount" label={t('jobs:expenseIncome.amount')}>
              <input
                id="edit-expense-amount"
                type="number"
                min="0"
                step="0.01"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
            <Field id="edit-expense-date" label={t('jobs:expenseIncome.date')}>
              <input
                id="edit-expense-date"
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
            <Field id="edit-expense-category" label={t('jobs:expenseIncome.category')}>
              <input
                id="edit-expense-category"
                type="text"
                required
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
            <Field id="edit-expense-vendor" label={t('jobs:expenseIncome.vendor')}>
              <input
                id="edit-expense-vendor"
                type="text"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={pending}>
              {t('jobs:expenseIncome.save')}
            </Button>
            <Button type="button" variant="secondary" onClick={onCancel}>
              {t('jobs:expenseIncome.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddExpenseForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onSubmit: (input: ExpenseInput) => void;
}) {
  const { t } = useTranslation();
  const amountId = useId();
  const dateId = useId();
  const categoryId = useId();
  const vendorId = useId();
  const receiptId = useId();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState('');
  const [vendor, setVendor] = useState('');
  const [receiptFilename, setReceiptFilename] = useState<string | null>(null);
  const [receiptName, setReceiptName] = useState<string | null>(null);
  const [suggested, setSuggested] = useState(false);

  const uploadReceipt = useUploadReceiptMutation();

  // Bail out of any in-flight poll on unmount so no state is applied after teardown.
  const cancelledRef = useRef(false);
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  /** Prefill only EMPTY fields from the OCR suggestions — never overwrite typed values. */
  function applySuggestions(parsed: ReceiptSuggestions | null) {
    if (!parsed) return;
    let didSuggest = false;
    if (parsed.total != null) {
      setAmount((prev) => {
        if (prev !== '') return prev;
        didSuggest = true;
        return String(parsed.total);
      });
    }
    if (parsed.vendorName) {
      setVendor((prev) => {
        if (prev !== '') return prev;
        didSuggest = true;
        return parsed.vendorName as string;
      });
    }
    if (parsed.suggestedCategory) {
      setCategory((prev) => {
        if (prev !== '') return prev;
        didSuggest = true;
        return parsed.suggestedCategory as string;
      });
    }
    if (didSuggest) setSuggested(true);
  }

  function onReceiptChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    cancelledRef.current = false;
    setSuggested(false);
    setReceiptName(file.name);
    const formData = new FormData();
    formData.set('receipt', file);
    uploadReceipt.mutate(formData, {
      onSuccess: (result) => {
        setReceiptFilename(result.receipt.receiptFilename);
        if (result.receipt.parsed) {
          applySuggestions(result.receipt.parsed);
        }
        if (result.receipt.status === 'pending') {
          void pollReceiptOcr(result.receipt.receiptFilename, () => !cancelledRef.current)
            .then((parsed) => {
              if (!cancelledRef.current) applySuggestions(parsed);
            })
            .catch((err) => console.error('receipt ocr poll failed', err));
        }
      },
    });
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit({
      amount: Number(amount),
      date,
      category: category || undefined,
      vendor: vendor || undefined,
      receiptFilename: receiptFilename ?? undefined,
    });
    setAmount('');
    setCategory('');
    setVendor('');
    setReceiptFilename(null);
    setReceiptName(null);
    setSuggested(false);
  }

  return (
    <form onSubmit={submit} className="rounded-md border border-border bg-card p-6">
      <h3 className="mb-4 font-semibold text-foreground text-lg">
        {t('jobs:expenseIncome.addExpenseHeading')}
      </h3>
      {error ? (
        <p role="alert" className="mb-3 text-destructive text-sm">
          {error}
        </p>
      ) : null}
      {uploadReceipt.isError ? (
        <p role="alert" className="mb-3 text-destructive text-sm">
          {t('jobs:expenseIncome.receiptUploadError')}
        </p>
      ) : null}
      <div className="mb-3 flex flex-col gap-1">
        <label htmlFor={receiptId} className="font-medium text-foreground text-sm">
          {t('jobs:expenseIncome.receipt')}
        </label>
        <input
          id={receiptId}
          type="file"
          accept="image/*,application/pdf"
          onChange={onReceiptChange}
          disabled={uploadReceipt.isPending}
          className="text-foreground text-sm file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:font-medium file:text-secondary-foreground file:text-sm"
        />
        {uploadReceipt.isPending ? (
          <p className="text-muted-foreground text-xs">
            {t('jobs:expenseIncome.receiptUploading')}
          </p>
        ) : receiptName ? (
          <p className="text-secondary-foreground text-xs">
            {t('jobs:expenseIncome.receiptAttached', { name: receiptName })}
          </p>
        ) : null}
      </div>
      {suggested ? (
        <p className="mb-3 text-muted-foreground text-xs">
          {t('jobs:expenseIncome.receiptSuggested')}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id={amountId} label={t('jobs:expenseIncome.amount')}>
          <input
            id={amountId}
            type="number"
            min="0"
            step="0.01"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>
        <Field id={dateId} label={t('jobs:expenseIncome.date')}>
          <input
            id={dateId}
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>
        <Field id={categoryId} label={t('jobs:expenseIncome.category')}>
          <input
            id={categoryId}
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>
        <Field id={vendorId} label={t('jobs:expenseIncome.vendor')}>
          <input
            id={vendorId}
            type="text"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>
      </div>
      <div className="mt-4">
        <Button type="submit" disabled={pending}>
          {t('jobs:expenseIncome.addExpense')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Recording money received on a job.
 *
 * ⚠ THE INVOICE PICKER IS REQUIRED, and "None" is one of its answers. A job's income and its
 * invoice payments were separate entries for the same check: the payment settled the customer's
 * bill, the income row fed profit, and the office typed both. Choosing an invoice here records a
 * real payment against it and the income row is written as that payment's mirror — one entry, both
 * figures right.
 *
 * It asks rather than guessing because a payment is customer-facing: it recomputes the invoice's
 * status, so picking the wrong bill marks something Paid that was never settled. "None" stays
 * available because income that never came through an invoice is ordinary and must remain possible.
 */
function AddIncomeForm({
  jobId,
  pending,
  error,
  onSubmit,
}: {
  jobId: number;
  pending: boolean;
  error: string | null;
  onSubmit: (input: {
    amount: number;
    date: string;
    description?: string;
    invoiceId?: number | null;
  }) => void;
}) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();
  const amountId = useId();
  const dateId = useId();
  const descriptionId = useId();
  const invoiceId = useId();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  // '' is "not answered yet" and 'none' is the deliberate answer, so the two are never confused
  // and the form cannot be submitted having skipped the question.
  const [applyTo, setApplyTo] = useState('');

  const openInvoices = useJobOpenInvoicesQuery(jobId);
  const invoices = openInvoices.data?.invoices ?? [];
  // An empty picker used to explain nothing, which is how this read as broken rather than as a
  // missing step: "Invoice balance" attaches a real numbered invoice to the job, but it is a DRAFT,
  // and a draft is deliberately withheld here (money must not land on a document the customer has
  // never seen). So the operator saw a bill on the job and no way to apply a payment to it, with
  // nothing on screen connecting the two. Only shown once the query has actually answered — a hint
  // rendered during the fetch would flash on every open.
  const noneToApplyTo = openInvoices.isSuccess && invoices.length === 0;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit({
      amount: Number(amount),
      date,
      description: description || undefined,
      invoiceId: applyTo === '' || applyTo === 'none' ? null : Number(applyTo),
    });
    setAmount('');
    setDescription('');
    setApplyTo('');
  }

  return (
    <form onSubmit={submit} className="rounded-md border border-border bg-card p-6">
      <h3 className="mb-4 font-semibold text-foreground text-lg">
        {t('jobs:expenseIncome.addIncomeHeading')}
      </h3>
      {error ? (
        <p role="alert" className="mb-3 text-destructive text-sm">
          {error}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id={amountId} label={t('jobs:expenseIncome.amount')}>
          <input
            id={amountId}
            type="number"
            min="0"
            step="0.01"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>
        <Field id={dateId} label={t('jobs:expenseIncome.date')}>
          <input
            id={dateId}
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>
        <Field id={descriptionId} label={t('jobs:expenseIncome.description')}>
          <input
            id={descriptionId}
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>
        <Field id={invoiceId} label={t('jobs:expenseIncome.applyToInvoice')}>
          <select
            id={invoiceId}
            required
            value={applyTo}
            onChange={(e) => setApplyTo(e.target.value)}
            className={INPUT_CLASS}
          >
            <option value="" disabled>
              {t('jobs:expenseIncome.applyToChoose')}
            </option>
            {invoices.map((invoice) => (
              <option key={invoice.id} value={String(invoice.id)}>
                {invoice.invoiceNumber ?? `#${invoice.id}`} —{' '}
                {fmt.formatCurrency(invoice.openBalance)} {t('jobs:expenseIncome.applyToOwing')}
              </option>
            ))}
            <option value="none">{t('jobs:expenseIncome.applyToNone')}</option>
          </select>
          {noneToApplyTo ? (
            <p className="text-muted-foreground text-sm">
              {t('jobs:expenseIncome.applyToNoneAvailable')}
            </p>
          ) : null}
        </Field>
      </div>
      {applyTo !== '' && applyTo !== 'none' ? (
        <p className="mt-3 text-muted-foreground text-sm">
          {t('jobs:expenseIncome.applyToNotice')}
        </p>
      ) : null}
      <div className="mt-4">
        <Button type="submit" disabled={pending}>
          {t('jobs:expenseIncome.addIncome')}
        </Button>
      </div>
    </form>
  );
}

const INPUT_CLASS =
  'h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-medium text-foreground text-sm">
        {label}
      </label>
      {children}
    </div>
  );
}
